import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, MapPin, Clock, Phone, CheckCircle } from 'lucide-react';
import { SITE } from '../../data/site';
import { Reveal } from '../../components/Bits';
import { Img } from '../../components/Img';

const PAGE_URL = 'https://biryanify.us/blog/best-restaurants-irving-tx-open-now';
const TITLE = 'Best Restaurants in Irving TX Open Now | Pickup & Dine-In Options | Biryanify';
const DESCRIPTION =
  'Looking for a restaurant in Irving TX open now for pickup or dine-in? Discover why Biryanify is the top choice for authentic Hyderabadi biryani, mandi, and Indian cuisine near Las Colinas, Valley Ranch, and Irving Mall.';

const FAQS = [
  {
    q: 'What does "restaurant near me open now" mean?',
    a: 'When you search "restaurant near me open now" or "restaurant near me open," you are looking for dining options that are currently accepting orders and serving customers at that exact moment. It is a real-time search for immediate availability, not future reservations.',
  },
  {
    q: 'Is Biryanify open right now for pickup?',
    a: 'Biryanify is open daily from 11 AM–10 PM (Sunday–Thursday) and 11 AM–11 PM (Friday–Saturday). You can always check the current status on the website or call (214) 377-8283 to confirm, then order online for immediate pickup or walk in to order at the counter.',
  },
  {
    q: 'How long does it take to pick up an order at Biryanify?',
    a: 'Typical pickup time is 15–20 minutes after ordering, depending on how busy the kitchen is. Biryani is cooked fresh to order (45+ minutes if you order it), but quicker items like dosa, kebabs, and mandi are ready faster. You will see an estimated time when you place your order online.',
  },
  {
    q: 'Does Biryanify in Irving take online orders for pickup?',
    a: 'Yes. You can browse the full menu, add items, and place an order for pickup directly through the website. You will get a confirmation and an estimated pickup time. Then just show up and collect your food at the counter.',
  },
  {
    q: 'What Indian restaurants in Irving TX serve the best biryani?',
    a: 'Biryanify specializes in slow-cooked, dum-style Hyderabadi biryani—the authentic kind that is layered raw and sealed for 45+ minutes. Many casual Indian restaurants steam biryani in a warming tray, which is faster but less flavorful. If you want the real deal, Biryanify is worth the visit in Irving.',
  },
  {
    q: 'Can I eat at Biryanify without ordering online?',
    a: 'Absolutely. Biryanify is walk-in friendly. Come in anytime during business hours (11 AM–10 PM most days, until 11 PM Fri–Sat), order at the counter, and eat in the dining area while your food is being prepared. No reservations needed.',
  },
  {
    q: 'Is Biryanify near Irving Mall or Las Colinas?',
    a: 'Yes. Biryanify is located at 9400 N MacArthur Blvd, Irving, which is just minutes from Irving Mall, Las Colinas business park, and Valley Ranch residential area. If you are in those neighborhoods and searching "restaurant near me," Biryanify is a quick drive away.',
  },
];

export default function BestRestaurantsIrvingTxOpenNow() {
  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png" />
        <meta property="og:image:alt" content="Biryanify restaurant in Irving TX serving Indian biryani, mandi and kebabs open for pickup and dine-in" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://biryaniify.us/' },
                { '@type': 'ListItem', position: 2, name: 'Best Restaurants in Irving TX Open Now', item: PAGE_URL },
              ],
            },
            {
              '@type': 'Article',
              headline: 'Best Restaurants in Irving TX Open Now: Pickup & Dine-In Guide',
              description: DESCRIPTION,
              author: { '@type': 'Organization', name: 'Biryanify' },
              publisher: { '@type': 'Organization', name: 'Biryanify' },
              mainEntityOfPage: PAGE_URL,
              url: PAGE_URL,
              datePublished: '2025-01-15',
              dateModified: '2025-01-15',
              image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png',
            },
            {
              '@type': 'FAQPage',
              mainEntity: FAQS.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            },
          ],
        }) }} />
      </Helmet>
      <div style={{ background: 'var(--color-surface)' }}>
        <section className="relative min-h-[46vh] flex flex-col justify-end overflow-hidden">
          <Img
            src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png"
            alt="Biryanify restaurant in Irving TX open now for pickup and dine-in service, serving authentic Indian biryani and mandi"
            eager
            className="absolute inset-0"
            imgClassName="scale-105"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(20,13,8,0.6) 0%, rgba(20,13,8,0.25) 45%, rgba(20,13,8,0.92) 100%)' }} />
          <div className="wrap relative pb-12 pt-32 md:pb-16">
            <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>Irving, TX Dining</p>
            <h1 className="font-display mt-3 text-white" style={{ fontSize: 'clamp(2.2rem, 5.6vw, 3.6rem)', lineHeight: 1.05, textShadow: '0 2px 24px rgba(0,0,0,0.4)' }}>
              Best Restaurants in Irving, TX Open Now
            </h1>
            <p className="mt-4 max-w-2xl" style={{ fontSize: '1.08rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}>
              Searching &ldquo;restaurant near me open now&rdquo; in Irving? Here&rsquo;s what to know about getting authentic Indian food, fast.
            </p>
          </div>
        </section>

        <article className="wrap py-14 md:py-20 max-w-3xl mx-auto">
          <Reveal>
            <p className="text-ink-2" style={{ fontSize: '1.08rem', lineHeight: 1.75 }}>
              If you&rsquo;ve typed &ldquo;restaurant near me open now,&rdquo; &ldquo;restaurant in Irving TX,&rdquo; or &ldquo;restaurant near me dine-in&rdquo; into Google tonight, you&rsquo;re probably hungry and need an answer fast. Irving, Texas—especially around Las Colinas, Irving Mall, and the Valley Ranch area—has no shortage of quick-service and casual dining options. But finding a restaurant that serves <strong>authentic, made-to-order Indian food</strong> right now is rarer than you might think.
            </p>
            <p className="mt-4 text-ink-2" style={{ fontSize: '1.08rem', lineHeight: 1.75 }}>
              This guide walks you through what &ldquo;restaurant near me open now&rdquo; actually means, why it matters for your dinner plans tonight, and why <Link to="/" className="underline font-semibold">Biryanify</Link> at 9400 N MacArthur Blvd is the best answer if you&rsquo;re craving Hyderabadi biryani, Arabian mandi, or South Indian kebabs in Irving right now.
            </p>
          </Reveal>

          <Reveal delay={80}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">What Does "Restaurant Near Me Open Now" Mean?</h2>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              When you search "restaurant near me open now," you are asking for real-time availability. Google interprets this as:
            </p>
            <ul className="mt-5 space-y-3 text-ink-2" style={{ lineHeight: 1.7 }}>
              <li><strong className="text-ink">Physically close to your location</strong> (usually within a few miles or a 10–15 minute drive).</li>
              <li><strong className="text-ink">Currently accepting orders</strong> during their posted business hours.</li>
              <li><strong className="text-ink">Able to serve you immediately</strong> or within a short wait (not closed, not at capacity).</li>
              <li><strong className="text-ink">Offers pickup, delivery, or dine-in</strong> that matches how you want to eat.</li>
            </ul>
            <p className="mt-5 text-ink-2" style={{ lineHeight: 1.75 }}>
              The beauty of "restaurant near me open now" searches is that they eliminate the question marks. You don't have to call ahead to check hours or drive across town hoping they're open. The search does the work for you.
            </p>
          </Reveal>

          <Reveal delay={100}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Why Authenticity Matters When You Search "Restaurant Near Me"</h2>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              Irving has plenty of Indian restaurants, but there's a difference between <strong>fast-casual Indian food</strong> and <strong>authentically prepared Indian cuisine</strong>. When you search "restaurant in Irving TX" or "best restaurant Irving TX," you are often scrolling through chains that:
            </p>
            <ul className="mt-5 space-y-3 text-ink-2" style={{ lineHeight: 1.7 }}>
              <li><strong className="text-ink">Use steam-table biryani</strong> kept warm under heat lamps instead of cooking it fresh to order.</li>
              <li><strong className="text-ink">Prioritize speed over technique,</strong> serving pre-made dishes rather than individual orders cooked in real time.</li>
              <li><strong className="text-ink">Lack regional variety</strong>—they have "biryani," but not Hyderabadi dum biryani specifically.</li>
            </ul>
            <p className="mt-5 text-ink-2" style={{ lineHeight: 1.75 }}>
              <Link to="/about" className="underline font-semibold">Biryanify exists to flip that script.</Link> We slow-cook biryani the Hyderabadi way (sealed, on dum, 45+ minutes). We grill mandi fresh over coals. We make dosa and idli to order every single day. When you search "restaurant near me open now" and find Biryanify, you are getting authentic food—not shortcuts.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Biryanify Hours: Always Here When You Search "Open Now"</h2>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              One of the biggest frustrations with "restaurant near me open now" searches is finding a place that says it is open but then isn't ready to serve you. <Link to="/visit" className="underline font-semibold">Biryanify keeps consistent hours</Link> every single day:
            </p>
            <div className="mt-6 card p-6 md:p-7 grid gap-4">
              <div className="flex items-start gap-3">
                <Clock size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-ink">Sunday–Thursday</div>
                  <div className="text-ink-2">11:00 AM – 10:00 PM</div>
                  <div className="text-sm text-ink-3 mt-1">Perfect for lunch, after-work pickup, or dinner.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-ink">Friday–Saturday</div>
                  <div className="text-ink-2">11:00 AM – 11:00 PM</div>
                  <div className="text-sm text-ink-3 mt-1">Extended hours for weekend dining and late-night pickup.</div>
                </div>
              </div>
            </div>
            <p className="mt-5 text-ink-2" style={{ lineHeight: 1.75 }}>
              No sudden closures, no "out of ingredients," no "kitchen closed early." When Biryanify is listed as open, we are <strong>actively cooking and ready to serve.</strong>
            </p>
          </Reveal>

          <Reveal delay={140}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">How to Order: Pickup vs. Dine-In vs. Delivery</h2>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              When you find Biryanify through "restaurant near me open now," you have three ways to get your food:
            </p>
            <div className="mt-6 space-y-4">
              <div className="card p-6">
                <h3 className="font-display text-lg text-ink flex items-start gap-3">
                  <CheckCircle size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
                  Online Pickup (Recommended for Speed)
                </h3>
                <p className="mt-3 text-ink-2" style={{ lineHeight: 1.7 }}>
                  Order through <Link to="/menu" className="underline font-semibold">our menu page</Link>, pay online, and pick up your food at the counter. Typical wait: 15–20 minutes. You will get a confirmation with an estimated time, so you can time your arrival perfectly.
                </p>
              </div>
              <div className="card p-6">
                <h3 className="font-display text-lg text-ink flex items-start gap-3">
                  <CheckCircle size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
                  Walk-In Dine-In (No Wait, No Reservation Needed)
                </h3>
                <p className="mt-3 text-ink-2" style={{ lineHeight: 1.7 }}>
                  If you are in the area, walk right in. Order at the counter, grab a seat in our dining area, and we will bring your food as soon as it is ready. Biryani takes time (45+ minutes if you order it), but quicker items come out fast.
                </p>
              </div>
              <div className="card p-6">
                <h3 className="font-display text-lg text-ink flex items-start gap-3">
                  <CheckCircle size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
                  Phone Order for Pickup
                </h3>
                <p className="mt-3 text-ink-2" style={{ lineHeight: 1.7 }}>
                  Call <a href={SITE.phoneHref} className="underline font-semibold">{SITE.phone}</a> to order by phone. Let the team know when you will pick up, and we will time your food so it comes hot and fresh.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={160}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Location: Where to Find Biryanify When You Search "Restaurant Near Me"</h2>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              If you are searching "restaurant near me" from anywhere in Irving—especially Las Colinas, Valley Ranch, or near Irving Mall—Biryanify is likely to show up. Here is exactly where we are:
            </p>
            <div className="mt-6 card p-6 md:p-7">
              <div className="flex items-start gap-3">
                <MapPin size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-ink text-lg">{SITE.address.line1}</div>
                  <div className="text-ink-2 text-base">{SITE.address.line2}</div>
                  <div className="text-ink-3 text-sm mt-2">
                    <strong>Landmarks:</strong> Minutes from Irving Mall, Las Colinas business park, Valley Ranch residential area.
                  </div>
                  <div className="mt-3">
                    <a href={`https://maps.google.com/?q=${SITE.address.line1.replace(/\s/g, '+')}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-ghost">
                      Get directions on Google Maps
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={180}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">The Biryanify Menu: What to Order Right Now</h2>
            <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
              When you find Biryanify through "restaurant near me open now," what should you actually order? Here is a quick breakdown:
            </p>
            <div className="mt-6 space-y-4">
              <div className="card p-6" style={{ borderLeft: '4px solid var(--color-accent)' }}>
                <h3 className="font-display text-lg text-ink">For Authentic Slow-Cooked Flavor: Hyderabadi Dum Biryani</h3>
                <p className="mt-2 text-ink-2" style={{ lineHeight: 1.7 }}>
                  If you have 45+ minutes and want the "wow" meal, order chicken, goat, or vegetable dum biryani. It is marinated, layered, sealed, and slow-cooked to absolute perfection.
                </p>
              </div>
              <div className="card p-6" style={{ borderLeft: '4px solid var(--color-accent)' }}>
                <h3 className="font-display text-lg text-ink">For Quick, Smoky Satisfaction: Al Faham Mandi</h3>
                <p className="mt-2 text-ink-2" style={{ lineHeight: 1.7 }}>
                  Grilled chicken over saffron rice with a garlic-lemon sauce. Ready faster than biryani but equally memorable. This is the go-to for "restaurant near me open now" moments.
                </p>
              </div>
              <div className="card p-6" style={{ borderLeft: '4px solid var(--color-accent)' }}>
                <h3 className="font-display text-lg text-ink">For Lighter, South Indian Vibes: Dosa or Idli</h3>
                <p className="mt-2 text-ink-2" style={{ lineHeight: 1.7 }}>
                  Crispy, thin crepes (dosa) or steamed rice cakes (idli) with sambar and coconut chutney. Made fresh, ready fast, and deeply satisfying.
                </p>
              </div>
              <div className="card p-6" style={{ borderLeft: '4px solid var(--color-accent)' }}>
                <h3 className="font-display text-lg text-ink">For Appetizers & Sides: Kebabs, Chicken 65, Samosa</h3>
                <p className="mt-2 text-ink-2" style={{ lineHeight: 1.7 }}>
                  Fiery chicken 65, seekh kebab, and crispy samosa are all made fresh to order and ready in 10–15 minutes. Perfect for starting a meal or grabbing quick protein.
                </p>
              </div>
            </div>
            <p className="mt-6 text-ink-2" style={{ lineHeight: 1.75 }}>
              <Link to="/menu" className="underline font-semibold">Browse the full menu now</Link> and see all options, prices, and dietary tags (veg, egg, non-veg).
            </p>
          </Reveal>

          <Reveal delay={200}>
            <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Frequently Asked Questions</h2>
            <div className="mt-6 space-y-6">
              {FAQS.map((f) => (
                <div key={f.q} className="card p-6">
                  <h3 className="font-display text-lg text-ink">{f.q}</h3>
                  <p className="mt-2 text-ink-2" style={{ lineHeight: 1.7 }}>{f.a}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={220}>
            <div className="mt-14 card p-8 text-center" style={{ background: 'var(--color-surface-2)' }}>
              <h2 className="font-display text-2xl text-ink">Search "restaurant near me open now" and find us.</h2>
              <p className="mt-3 text-ink-2">Biryanify is open every day, ready to cook authentic Indian food for you. Order online for quick pickup, walk in for dine-in, or call to order by phone.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to="/menu" className="btn btn-primary">Order now <ArrowRight size={16} /></Link>
                <a href={SITE.phoneHref} className="btn btn-ghost">{SITE.phone}</a>
              </div>
            </div>
          </Reveal>
        </article>
      </div>
    </>
  );
}
